import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEvent } from '../context/EventContext';
import { useUserProfile } from '../hooks/useUserProfile';
import Button from '../components/Button';
import CustomSelect from '../components/CustomSelect';
import AddChecklistItemModal from '../components/client/AddChecklistItemModal';
import NotificationModal from '../components/NotificationModal';
import enhancedApiClient from '../api/enhancedApiClient';

import TaskDetailModal from '../components/TaskDetailModal';

const Checklist = () => {
  const { eventData, tasks, refreshTasks, loading: eventLoading } = useEvent();
  const location = useLocation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Items');
  const [vendorFilter, setVendorFilter] = useState('All Vendors');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'completed', 'overdue', 'upcoming'
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', '6plus', '3to6', '1to3', 'final'
  const [collapsedSections, setCollapsedSections] = useState({});
  const [vendors, setVendors] = useState([]);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const navigate = useNavigate();
  
  // Get current user from AWS Cognito
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);

  // Load vendors for filter dropdown
  useEffect(() => {
    const loadCreators = async () => {
      if (tasks.length === 0) return;
      const uniqueCreators = new Set();
      const creatorNames = {};
      
      for (const task of tasks) {
        if (task.createdByUserId && !creatorNames[task.createdByUserId]) {
          // Skip API call for system-generated tasks
          if (task.createdByUserId === 'system') {
            creatorNames['system'] = 'Your Planning Guide';
            continue;
          }
          try {
            const profile = await enhancedApiClient.getUser(task.createdByUserId);
            // If user has a vendor association, get the vendor name
            if (profile?.currentVendorId) {
              const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${profile.currentVendorId}`);
              if (vendorResponse.ok) {
                const vendorData = await vendorResponse.json();
                creatorNames[task.createdByUserId] = vendorData.name?.S || vendorData.name || profile.name || 'Unknown';
              } else {
                creatorNames[task.createdByUserId] = profile.name || 'Unknown';
              }
            } else if (task.createdByUserId === 'testclient1') {
              creatorNames[task.createdByUserId] = 'You';
            } else {
              creatorNames[task.createdByUserId] = profile?.vendorName || profile?.name || 'Unknown';
            }
          } catch (error) {
            console.log(`Could not load profile for ${task.createdByUserId}`);
            creatorNames[task.createdByUserId] = 'Unknown';
          }
        }
      }
      
      // Get unique creator names
      for (const task of tasks) {
        const creatorName = creatorNames[task.createdByUserId];
        if (creatorName && creatorName !== 'Unknown') {
          uniqueCreators.add(creatorName);
        }
      }
      
      setVendors(Array.from(uniqueCreators).map(name => ({ name })));
    };
    loadCreators();
  }, [tasks, userProfiles]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('taskId');
    if (taskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSelectedTask(task);
        setShowDetailView(true);
      }
    } else if (!hasAutoOpened && tasks.length > 0 && !taskId) {
      setHasAutoOpened(true);
      // Auto-collapse sections not relevant — keep the earliest uncompleted phase open
      const phases = ['6plus', '3to6', '1to3', 'final'];
      let currentPhase = null;
      for (const phase of phases) {
        const hasTasks = tasks.some(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === phase);
        if (hasTasks) { currentPhase = phase; break; }
      }
      if (currentPhase) {
        const collapsed = { completed: true };
        for (const phase of phases) {
          if (phase !== currentPhase) collapsed[phase] = true;
        }
        setCollapsedSections(collapsed);
      }
    }
  }, [location.search, tasks, hasAutoOpened]);

  // Load user profiles for creator names
  useEffect(() => {
    const loadUserProfiles = async () => {
      const profiles = {};
      for (const task of tasks) {
        if (task.createdByUserId && !profiles[task.createdByUserId]) {
          if (task.createdByUserId === 'system') continue;
          try {
            const profile = await enhancedApiClient.getUser(task.createdByUserId);
            profiles[task.createdByUserId] = profile;
          } catch (error) {
            console.log(`Could not load profile for ${task.createdByUserId}`);
          }
        }
        // Also load vendor details if vendorId exists
        if (task.vendorId && !profiles[task.vendorId]) {
          try {
            const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${task.vendorId}`);
            const vendor = await response.json();
            profiles[task.vendorId] = vendor;
          } catch (error) {
            console.log(`Could not load vendor for ${task.vendorId}`);
          }
        }
      }
      setUserProfiles(profiles);
    };
    
    if (tasks.length > 0) {
      loadUserProfiles();
    }
  }, [tasks]);

  const getCreatorName = (createdByUserId, vendorId) => {
    // System-generated planning tasks
    if (createdByUserId === 'system') return 'Your Planning Guide';
    
    // Check for test users first (before profile lookup)
    if (createdByUserId === 'testclient1') return 'You';
    if (createdByUserId === 'testvenue1-user') return 'Orakei Bay';
    if (createdByUserId === 'testvendor1-user') return 'Sakshin\'s Flower Shop';
    
    // Then try to get from createdByUserId profile
    if (createdByUserId) {
      const profile = userProfiles[createdByUserId];
      if (profile?.vendorName) return profile.vendorName;
      if (profile?.name) return profile.name;
    }
    
    // Then try vendorId
    if (vendorId) {
      const vendor = userProfiles[vendorId];
      if (vendor?.name) return vendor.name;
    }
    
    // Generic fallback
    if (createdByUserId?.includes('vendor')) return 'Vendor';
    if (createdByUserId?.includes('venue')) return 'Venue';
    
    return 'Unknown';
  };

  const getTaskResponsibilityStatus = (task, currentUserId) => {
    const isCreatedByClient = task.createdByUserId === currentUserId;
    const isSystemTask = task.createdByUserId === 'system';
    const isCompleted = task.status === 'completed';
    const vendorName = getCreatorName(task.createdByUserId, task.vendorId);
    
    // System-generated planning tasks — client owns these
    if (isSystemTask) {
      if (isCompleted) {
        return {
          text: 'Completed',
          color: '#059669',
          bgColor: '#D1FAE5'
        };
      } else {
        return {
          text: 'Pending Your Action',
          color: '#6B7280',
          bgColor: '#F3F4F6'
        };
      }
    }
    
    // Task created by vendor/external party
    if (!isCreatedByClient) {
      if (isCompleted) {
        return {
          text: `Completed - Awaiting ${vendorName} Confirmation`,
          color: '#F59E0B',
          bgColor: '#FEF3C7'
        };
      } else {
        return {
          text: `Awaiting Your Action`,
          color: '#DC2626',
          bgColor: '#FEE2E2'
        };
      }
    }
    
    // Task created by client
    if (isCreatedByClient) {
      if (isCompleted) {
        return {
          text: `Completed by ${vendorName}`,
          color: '#059669',
          bgColor: '#D1FAE5'
        };
      } else {
        // Handle "You" case specially
        if (vendorName === 'You') {
          return {
            text: `Pending Your Action`,
            color: '#6B7280',
            bgColor: '#F3F4F6'
          };
        } else {
          return {
            text: `Pending ${vendorName}'s Action`,
            color: '#6B7280',
            bgColor: '#F3F4F6'
          };
        }
      }
    }
    
    return null;
  };

  const getDueDateInfo = (dueDate, status) => {
    if (!dueDate || status === 'completed') return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return {
        text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`,
        color: '#DC2626',
        isOverdue: true
      };
    } else if (diffDays === 0) {
      return {
        text: 'Due today',
        color: '#F59E0B',
        isOverdue: false
      };
    } else if (diffDays === 1) {
      return {
        text: 'Due tomorrow',
        color: '#6B7280',
        isOverdue: false
      };
    } else {
      return {
        text: `Due in ${diffDays} days`,
        color: '#6B7280',
        isOverdue: false
      };
    }
  };

  const getTimeGroup = (dueDate, eventDate) => {
    if (!dueDate || !eventDate) return null;
    
    const due = new Date(dueDate);
    const event = new Date(eventDate);
    const diffTime = event - due;
    const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30);
    
    if (diffMonths < 1) return 'final';
    if (diffMonths < 3) return '1to3';
    if (diffMonths < 6) return '3to6';
    return '6plus';
  };

  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchQuery && !task.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Tab filter
    if (activeTab === 'completed' && task.status !== 'completed') return false;
    if (activeTab === 'overdue') {
      if (task.status === 'completed' || !task.dueDate) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(task.dueDate);
      due.setHours(0, 0, 0, 0);
      if (due >= today) return false;
    }
    if (activeTab === 'upcoming' && task.status === 'completed') return false;
    
    // Time filter
    if (timeFilter !== 'all' && task.status !== 'completed') {
      const timeGroup = getTimeGroup(task.dueDate, eventData?.eventDate);
      if (timeGroup !== timeFilter) return false;
    }
    
    // Status filter
    if (statusFilter === 'Completed' && task.status !== 'completed') return false;
    if (statusFilter === 'Pending' && task.status === 'completed') return false;
    
    // Vendor filter
    if (vendorFilter !== 'All Vendors') {
      const creatorName = getCreatorName(task.createdByUserId, task.vendorId);
      if (creatorName !== vendorFilter) return false;
    }
    
    return true;
  }).sort((a, b) => {
    // Default sort by due date
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return new Date(a.dueDate) - new Date(b.dueDate);
  });

  // Counts for time filter tabs — always based on full task list, not filtered
  const allTaskCounts = {
    '6plus': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === '6plus').length,
    '3to6': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === '3to6').length,
    '1to3': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === '1to3').length,
    'final': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === 'final').length,
  };

  // Group tasks by time period (for rendering)
  const groupedTasks = {
    '6plus': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === '6plus'),
    '3to6': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === '3to6'),
    '1to3': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === '1to3'),
    'final': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate, eventData?.eventDate) === 'final'),
    'completed': filteredTasks.filter(t => t.status === 'completed')
  };

  const timeGroupLabels = {
    '6plus': '6+ Months Out',
    '3to6': '3-6 Months Out',
    '1to3': '1-3 Months Out',
    'final': 'Final Month'
  };

  if (eventLoading || profileLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading checklist...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        backgroundColor: '#E8F4E8',
        padding: '32px 24px 140px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '16px'
      }}>
        <h1 style={{ 
          fontSize: '42px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          Checklist
        </h1>
        
        {/* Checklist Stats Cards */}
        <div style={{ 
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px',
          width: '100%',
          maxWidth: '1200px',
          padding: '0 24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: activeTab === 'completed' ? '0 4px 12px rgba(107, 144, 128, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            border: activeTab === 'completed' ? '2px solid #6B9080' : '2px solid transparent',
            transition: 'all 0.2s'
          }}
          onClick={() => { setActiveTab(activeTab === 'completed' ? 'all' : 'completed'); setStatusFilter('All Items'); setTimeFilter('all'); }}
          onMouseOver={(e) => {
            if (activeTab !== 'completed') {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }
          }}
          onMouseOut={(e) => {
            if (activeTab !== 'completed') {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }
          }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4"></path>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {tasks.filter(t => t.status === 'completed').length} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>completed</span>
              </div>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '16px', color: '#6B7280' }}>→</div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: activeTab === 'overdue' ? '0 4px 12px rgba(107, 144, 128, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            border: activeTab === 'overdue' ? '2px solid #6B9080' : '2px solid transparent',
            transition: 'all 0.2s'
          }}
          onClick={() => { setActiveTab(activeTab === 'overdue' ? 'all' : 'overdue'); setStatusFilter('All Items'); setTimeFilter('all'); }}
          onMouseOver={(e) => {
            if (activeTab !== 'overdue') {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }
          }}
          onMouseOut={(e) => {
            if (activeTab !== 'overdue') {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }
          }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {tasks.filter(t => {
                  if (t.status === 'completed' || !t.dueDate) return false;
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const due = new Date(t.dueDate);
                  due.setHours(0, 0, 0, 0);
                  return due < today;
                }).length} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>overdue</span>
              </div>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '16px', color: '#6B7280' }}>→</div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: activeTab === 'upcoming' ? '0 4px 12px rgba(107, 144, 128, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            border: activeTab === 'upcoming' ? '2px solid #6B9080' : '2px solid transparent',
            transition: 'all 0.2s'
          }}
          onClick={() => { setActiveTab(activeTab === 'upcoming' ? 'all' : 'upcoming'); setStatusFilter('All Items'); setTimeFilter('all'); }}
          onMouseOver={(e) => {
            if (activeTab !== 'upcoming') {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }
          }}
          onMouseOut={(e) => {
            if (activeTab !== 'upcoming') {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }
          }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {tasks.filter(t => t.status !== 'completed').length} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>upcoming</span>
              </div>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '16px', color: '#6B7280' }}>→</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px 24px 24px'
      }}>
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <input
            type="text"
            placeholder="Search checklist..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: '#F9FAFB'
            }}
          />
          <CustomSelect 
            options={['All Items', 'Completed', 'Pending']}
            defaultValue="All Items"
            onChange={(value) => {
              setStatusFilter(value);
              setActiveTab('all');
            }}
          />
          <CustomSelect 
            options={['All Vendors', ...vendors.map(v => v.name)]}
            defaultValue="All Vendors"
            onChange={(value) => {
              setVendorFilter(value);
              setActiveTab('all');
            }}
          />
          <button
            onClick={() => setShowAddModal(true)}
            style={{ padding: '10px 20px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            + Add Item
          </button>
          <button
            onClick={() => {
              const csv = ['Task,Due Date,Status,Added By,Description,Sub-tasks,Attachments'];
              tasks.forEach(t => {
                const subTasksStr = (t.subTasks || []).map(st => `${st.completed ? '[x]' : '[ ]'} ${st.text}`).join('; ') || 'None';
                const attachCount = (t.attachments || []).length + (t.documentUrl ? 1 : 0);
                const desc = (t.details || t.description || '').replace(/"/g, '""');
                csv.push(`"${t.name}","${t.dueDate || ''}","${t.status || 'pending'}","${getCreatorName(t.createdByUserId, t.vendorId)}","${desc}","${subTasksStr}","${attachCount} file${attachCount !== 1 ? 's' : ''}"`);
              });
              const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${eventData?.name || 'event'}-decisions-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
            }}
            style={{ padding: '10px 20px', backgroundColor: 'white', color: '#6B9080', border: '1.5px solid #6B9080', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Export Decisions
          </button>
        </div>
        
        {/* Time Period Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '4px'
        }}>
          <button
            onClick={() => {
              setTimeFilter('all');
              setActiveTab('all');
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: timeFilter === 'all' ? '#6B9080' : 'white',
              color: timeFilter === 'all' ? 'white' : '#6B7280',
              border: timeFilter === 'all' ? 'none' : '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            All Times
          </button>
          <button
            onClick={() => {
              setTimeFilter('6plus');
              setActiveTab('all');
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: timeFilter === '6plus' ? '#6B9080' : 'white',
              color: timeFilter === '6plus' ? 'white' : '#6B7280',
              border: timeFilter === '6plus' ? 'none' : '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            6+ Months ({allTaskCounts['6plus']})
          </button>
          <button
            onClick={() => {
              setTimeFilter('3to6');
              setActiveTab('all');
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: timeFilter === '3to6' ? '#6B9080' : 'white',
              color: timeFilter === '3to6' ? 'white' : '#6B7280',
              border: timeFilter === '3to6' ? 'none' : '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            3-6 Months ({allTaskCounts['3to6']})
          </button>
          <button
            onClick={() => {
              setTimeFilter('1to3');
              setActiveTab('all');
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: timeFilter === '1to3' ? '#6B9080' : 'white',
              color: timeFilter === '1to3' ? 'white' : '#6B7280',
              border: timeFilter === '1to3' ? 'none' : '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            1-3 Months ({allTaskCounts['1to3']})
          </button>
          <button
            onClick={() => {
              setTimeFilter('final');
              setActiveTab('all');
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: timeFilter === 'final' ? '#6B9080' : 'white',
              color: timeFilter === 'final' ? 'white' : '#6B7280',
              border: timeFilter === 'final' ? 'none' : '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            Final Month ({allTaskCounts['final']})
          </button>
        </div>
      </div>

      {/* Checklist Items */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px 24px 24px',
        display: 'grid',
        gap: '16px'
      }}>
        {timeFilter === 'all' && filteredTasks.filter(t => t.status !== 'completed').length > 0 ? (
          // Show grouped view when "All Times" is selected
          <>
            {['6plus', '3to6', '1to3', 'final'].map(group => {
              const groupTasks = groupedTasks[group];
              if (groupTasks.length === 0) return null;
              const isCollapsed = collapsedSections[group];
              
              return (
                <div key={group} style={{ marginBottom: '24px' }}>
                  <div
                    onClick={() => setCollapsedSections(prev => ({ ...prev, [group]: !prev[group] }))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      marginBottom: isCollapsed ? '0' : '12px',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #E5E7EB'
                    }}
                  >
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                      {timeGroupLabels[group]} ({groupTasks.length})
                    </h3>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      <polyline points="6,9 12,15 18,9"/>
                    </svg>
                  </div>
                  {!isCollapsed && groupTasks.map((task, index) => {
                    const dueDateInfo = getDueDateInfo(task.dueDate, task.status);
                    const responsibilityStatus = getTaskResponsibilityStatus(task, currentUser?.username);
                    return (
                      <div 
                        key={task.id || index} 
                        onClick={() => {
                          setSelectedTask(task);
                          setShowDetailView(true);
                        }}
                        style={{
                          backgroundColor: dueDateInfo?.isOverdue ? '#FEF2F2' : 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          border: dueDateInfo?.isOverdue ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          marginBottom: '12px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = dueDateInfo?.isOverdue ? '#FEE2E2' : '#F9FAFB'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = dueDateInfo?.isOverdue ? '#FEF2F2' : 'white'}
                      >
                        <div style={{
                          width: '48px',
                          height: '48px',
                          backgroundColor: dueDateInfo?.isOverdue ? '#FEE2E2' : '#E8F4E8',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: dueDateInfo?.isOverdue ? '#DC2626' : '#6B9080'
                        }}>
                          {task.icon || (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={dueDateInfo?.isOverdue ? '#DC2626' : '#6B9080'} strokeWidth="1.5">
                              <path d="M9 11l3 3L22 4"></path>
                              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                            </svg>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {task.name}
                            {responsibilityStatus && (
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                color: responsibilityStatus.color,
                                backgroundColor: responsibilityStatus.bgColor,
                                padding: '4px 10px',
                                borderRadius: '12px'
                              }}>
                                {responsibilityStatus.text}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{task.dueDate || 'No due date'}</span>
                            {dueDateInfo && (
                              <>
                                <span>•</span>
                                <span style={{ color: dueDateInfo.color, fontWeight: '500' }}>
                                  {dueDateInfo.text}
                                </span>
                              </>
                            )}
                            <span>•</span>
                            <span>{getCreatorName(task.createdByUserId, task.vendorId)}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {task.documentUrl && (
                              <Button 
                                variant="outline" 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(task.documentUrl, '_blank');
                                }}
                              >
                                Open Document
                              </Button>
                            )}
                          </div>
                        </div>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: task.status === 'completed' ? '#6B9080' : '#E5E7EB',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: task.status === 'completed' ? 'white' : '#6B7280',
                          fontSize: '16px',
                          cursor: 'pointer'
                        }} onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const newStatus = task.status === 'completed' ? 'pending' : 'completed';
                            await enhancedApiClient.updateTask(eventData?.eventId, task.id, { status: newStatus });
                            await refreshTasks();
                            
                            setNotification({
                              show: true,
                              message: newStatus === 'completed' ? 'Task marked as completed!' : 'Task marked as pending',
                              type: 'success'
                            });
                          } catch (error) {
                            setNotification({
                              show: true,
                              message: 'Failed to update task. Please try again.',
                              type: 'error'
                            });
                          }
                        }}>
                          {task.status === 'completed' ? '✓' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            
            {/* Show completed tasks at the end */}
            {groupedTasks.completed.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div
                  onClick={() => setCollapsedSections(prev => ({ ...prev, completed: !prev.completed }))}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', marginBottom: collapsedSections.completed ? '0' : '12px',
                    paddingBottom: '8px', borderBottom: '2px solid #E5E7EB'
                  }}
                >
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                    Completed ({groupedTasks.completed.length})
                  </h3>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" style={{ transform: collapsedSections.completed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <polyline points="6,9 12,15 18,9"/>
                  </svg>
                </div>
                {!collapsedSections.completed && groupedTasks.completed.map((task, index) => {
                  const dueDateInfo = getDueDateInfo(task.dueDate, task.status);
                  const responsibilityStatus = getTaskResponsibilityStatus(task, currentUser?.username);
                  return (
                    <div 
                      key={task.id || index} 
                      onClick={() => {
                        setSelectedTask(task);
                        setShowDetailView(true);
                      }}
                      style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        border: '1px solid #E5E7EB',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        marginBottom: '12px',
                        opacity: 0.7
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#E8F4E8',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: '#6B9080'
                      }}>
                        {task.icon || (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                            <path d="M9 11l3 3L22 4"></path>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                          </svg>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {task.name}
                          {responsibilityStatus && (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: responsibilityStatus.color,
                              backgroundColor: responsibilityStatus.bgColor,
                              padding: '4px 10px',
                              borderRadius: '12px'
                            }}>
                              {responsibilityStatus.text}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{task.dueDate || 'No due date'}</span>
                          <span>•</span>
                          <span>{getCreatorName(task.createdByUserId, task.vendorId)}</span>
                        </div>
                      </div>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#6B9080',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '16px',
                        cursor: 'pointer'
                      }} onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await enhancedApiClient.updateTask(eventData?.eventId, task.id, { status: 'pending' });
                          await refreshTasks();
                          
                          setNotification({
                            show: true,
                            message: 'Task marked as pending',
                            type: 'success'
                          });
                        } catch (error) {
                          setNotification({
                            show: true,
                            message: 'Failed to update task. Please try again.',
                            type: 'error'
                          });
                        }
                      }}>
                        ✓
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : filteredTasks.length > 0 ? (
          filteredTasks.map((task, index) => {
            const dueDateInfo = getDueDateInfo(task.dueDate, task.status);
            const responsibilityStatus = getTaskResponsibilityStatus(task, currentUser?.username);
            return (
            <div 
              key={task.id || index} 
              onClick={() => {
                setSelectedTask(task);
                setShowDetailView(true);
              }}
              style={{
              backgroundColor: dueDateInfo?.isOverdue ? '#FEF2F2' : 'white',
              padding: '20px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              border: dueDateInfo?.isOverdue ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = dueDateInfo?.isOverdue ? '#FEE2E2' : '#F9FAFB'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = dueDateInfo?.isOverdue ? '#FEF2F2' : 'white'}
            >
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: dueDateInfo?.isOverdue ? '#FEE2E2' : '#E8F4E8',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                color: dueDateInfo?.isOverdue ? '#DC2626' : '#6B9080'
              }}>
                {task.icon || (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={dueDateInfo?.isOverdue ? '#DC2626' : '#6B9080'} strokeWidth="1.5">
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {task.name}
                  {responsibilityStatus && (
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: responsibilityStatus.color,
                      backgroundColor: responsibilityStatus.bgColor,
                      padding: '4px 10px',
                      borderRadius: '12px'
                    }}>
                      {responsibilityStatus.text}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{task.dueDate || 'No due date'}</span>
                  {dueDateInfo && (
                    <>
                      <span>•</span>
                      <span style={{ color: dueDateInfo.color, fontWeight: '500' }}>
                        {dueDateInfo.text}
                      </span>
                    </>
                  )}
                  <span>•</span>
                  <span>{getCreatorName(task.createdByUserId, task.vendorId)}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {task.documentUrl && (
                    <Button 
                      variant="outline" 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(task.documentUrl, '_blank');
                      }}
                    >
                      Open Document
                    </Button>
                  )}
                </div>
              </div>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: task.status === 'completed' ? '#6B9080' : '#E5E7EB',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: task.status === 'completed' ? 'white' : '#6B7280',
                fontSize: '16px',
                cursor: 'pointer'
              }} onClick={async (e) => {
                e.stopPropagation();
                try {
                  const newStatus = task.status === 'completed' ? 'pending' : 'completed';
                  await enhancedApiClient.updateTask(eventData?.eventId, task.id, { status: newStatus });
                  await refreshTasks();
                  
                  // Show success notification
                  setNotification({
                    show: true,
                    message: newStatus === 'completed' ? 'Task marked as completed!' : 'Task marked as pending',
                    type: 'success'
                  });
                } catch (error) {
                  setNotification({
                    show: true,
                    message: 'Failed to update task. Please try again.',
                    type: 'error'
                  });
                }
              }}>
                {task.status === 'completed' ? '✓' : ''}
              </div>
            </div>
          );
          })
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
            <h3>No Data</h3>
            <p>No checklist items available from backend.</p>
          </div>
        )}
      </div>

      <AddChecklistItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onItemAdded={async () => {
          await refreshTasks();
        }}
      />
      
      <NotificationModal
        isOpen={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />
      
      <TaskDetailModal
        task={showDetailView ? selectedTask : null}
        onClose={() => {
          setShowDetailView(false);
          setSelectedTask(null);
        }}
        eventId={eventData?.eventId}
        currentUser={currentUser}
        onStatusUpdate={refreshTasks}
      />
    </div>
  );
};

export default Checklist;